import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import {
  Box,
  Typography,
  Paper,
  Tooltip,
  CircularProgress,
} from '@material-ui/core'
import TrendingUpIcon from 'components/material-symbol/icons/TrendingUp'
import TrendingDownIcon from 'components/material-symbol/icons/TrendingDown'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import ErrorOutlineIcon from 'components/material-symbol/icons/ErrorOutline'
import CountUp from 'react-countup'
import { standardTileStyle } from '../styles'

const useStyles = makeStyles(theme => ({
  card: {
    ...standardTileStyle,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(3),
    backgroundColor: props => props.backgroundColor || '#ffffff',
    cursor: props => (props.onClick ? 'pointer' : 'default'),
  },
  header: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1, 0),
  },
  value: {
    fontSize: '2.5rem',
    fontWeight: 700,
    color: props => props.valueColor || '#00205B',
    marginBottom: theme.spacing(1),
    textAlign: 'center',
    lineHeight: 1.2,
    display: 'flex',
    alignItems: 'center',
  },
  label: {
    fontSize: '1rem',
    color: props => props.labelColor || 'rgba(0, 0, 0, 0.6)',
    textAlign: 'center',
    fontWeight: 500,
  },
  icon: {
    marginRight: theme.spacing(1),
    fontSize: '1.25rem',
    color: props => props.iconColor,
  },
  infoIcon: {
    color: theme.palette.text.secondary,
    fontSize: '1.25rem',
    cursor: 'help',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(1),
  },
  trend: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.875rem',
    fontWeight: 500,
    marginTop: theme.spacing(1),
  },
  trendUp: {
    color: theme.palette.success.main,
  },
  trendDown: {
    color: theme.palette.error.main,
  },
  trendIcon: {
    fontSize: '1.25rem',
    marginRight: theme.spacing(0.5),
  },
  errorIcon: {
    color: theme.palette.error.main,
    fontSize: '3rem',
    marginBottom: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '80px',
  },
}))

/**
 * An enhanced analytics card component for displaying metrics with additional features
 */
const EnhancedAnalyticsCard = ({
  value,
  label,
  valueColor,
  labelColor,
  icon,
  iconColor,
  backgroundColor,
  tooltip,
  trend,
  trendDirection,
  onClick,
  className,
  loading,
  error,
  useCountUp,
  countUpOptions,
  prefix,
  suffix,
  ...rest
}) => {
  const styleProps = {
    valueColor,
    labelColor,
    iconColor,
    backgroundColor,
    onClick,
  }
  const classes = useStyles(styleProps)

  // Render loading state
  if (loading) {
    return (
      <Paper
        className={`${classes.card} ${className || ''}`}
        elevation={1}
        {...rest}
      >
        <Box className={classes.loadingContainer}>
          <CircularProgress size={40} />
        </Box>
      </Paper>
    )
  }

  // Render error state
  if (error) {
    return (
      <Paper
        className={`${classes.card} ${className || ''}`}
        elevation={1}
        {...rest}
      >
        <Box className={classes.content}>
          <ErrorOutlineIcon className={classes.errorIcon} />
          <Typography variant="body1" className={classes.label}>
            Error loading data
          </Typography>
        </Box>
      </Paper>
    )
  }

  // Format the value with CountUp if specified
  const renderValue = () => {
    if (useCountUp && typeof value === 'number') {
      return (
        <>
          {prefix}
          <CountUp
            start={0}
            end={value}
            duration={2}
            separator=","
            decimals={2}
            decimal="."
            prefix=""
            useEasing={true}
            {...countUpOptions}
          />
          {suffix}
        </>
      )
    }
    return (
      <>
        {prefix}
        {value}
        {suffix}
      </>
    )
  }

  return (
    <Paper
      className={`${classes.card} ${className || ''}`}
      elevation={1}
      onClick={onClick}
      {...rest}
    >
      {tooltip && (
        <Box className={classes.header}>
          <Tooltip title={tooltip} placement="top">
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Tooltip>
        </Box>
      )}

      <Box className={classes.content}>
        <Typography variant="h2" className={classes.value}>
          {icon && <span className={classes.icon}>{icon}</span>}
          {renderValue()}
        </Typography>
        <Typography variant="body1" className={classes.label}>
          {label}
        </Typography>

        {trend && (
          <Box
            className={`${classes.trend} ${
              trendDirection === 'up'
                ? classes.trendUp
                : trendDirection === 'down'
                  ? classes.trendDown
                  : ''
            }`}
          >
            {trendDirection === 'up' && (
              <TrendingUpIcon className={classes.trendIcon} />
            )}
            {trendDirection === 'down' && (
              <TrendingDownIcon className={classes.trendIcon} />
            )}
            {trend}
          </Box>
        )}
      </Box>
    </Paper>
  )
}

EnhancedAnalyticsCard.propTypes = {
  /** The metric value to display (can be a string or number) */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** The label that describes the metric */
  label: PropTypes.node.isRequired,
  /** Optional color for the value (hex, rgb, or theme color) */
  valueColor: PropTypes.string,
  /** Optional color for the label (hex, rgb, or theme color) */
  labelColor: PropTypes.string,
  /** Optional icon element to display next to the value */
  icon: PropTypes.node,
  /** Color for the icon (hex, rgb, or theme color) */
  iconColor: PropTypes.string,
  /** Background color for the card (hex, rgb, or theme color) */
  backgroundColor: PropTypes.string,
  /** Optional tooltip text for the info icon */
  tooltip: PropTypes.string,
  /** Optional trend text to display at the bottom */
  trend: PropTypes.string,
  /** Direction of the trend ('up' or 'down') */
  trendDirection: PropTypes.oneOf(['up', 'down']),
  /** Handler for click events */
  onClick: PropTypes.func,
  /** Additional class name */
  className: PropTypes.string,
  /** Whether the card is in a loading state */
  loading: PropTypes.bool,
  /** Whether there's an error loading the data */
  error: PropTypes.bool,
  /** Whether to animate the value using CountUp */
  useCountUp: PropTypes.bool,
  /** Additional options for the CountUp component */
  countUpOptions: PropTypes.object,
  /** Text to display before the value */
  prefix: PropTypes.string,
  /** Text to display after the value */
  suffix: PropTypes.string,
}

EnhancedAnalyticsCard.defaultProps = {
  valueColor: '#00205B',
  labelColor: 'rgba(0, 0, 0, 0.6)',
  backgroundColor: '#ffffff',
  loading: false,
  error: false,
  useCountUp: false,
  countUpOptions: {},
  prefix: '',
  suffix: '',
}

export default EnhancedAnalyticsCard
